import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { adminApi } from '@/services/api';
import { MessageSquare, Mail, Calendar, ArrowLeft, Check, Globe, Briefcase, FolderKanban, FileText, Settings } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import '@/styles/AdminContacts.css';

const AdminContacts = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedContact, setSelectedContact] = useState(null);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    if (!adminApi.isAuthenticated()) {
      navigate('/admin/login');
      return;
    }
    loadContacts();
  }, [navigate]);

  const loadContacts = async () => {
    try {
      const data = await adminApi.getContacts();
      setContacts(data);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id) => {
    try {
      await adminApi.updateContactStatus(id, 'read');
      setContacts(contacts.map(c => c.id === id ? { ...c, status: 'read' } : c));
      toast({ title: "Marked as read" });
    } catch (error) {
      toast({ title: "Failed to update status", variant: "destructive" });
    }
  };

  if (loading) {
    return <div className="admin-loading">Loading...</div>;
  }

  return (
    <div className="admin-dashboard">
      <aside className="admin-sidebar">
        <div className="sidebar-header">
          <h2>AK Admin</h2>
        </div>
        <nav className="sidebar-nav">
          <button className="nav-item" onClick={() => navigate('/admin/dashboard')}>
            Dashboard
          </button>
          <button className="nav-item active">
            <MessageSquare size={20} />
            Messages
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/projects')}>
            <FolderKanban size={20} />
            Projects
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/articles')}>
            <FileText size={20} />
            Articles
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/experience')}>
            <Briefcase size={20} />
            Experience
          </button>
          <button className="nav-item" onClick={() => navigate('/admin/settings')}>
            <Settings size={20} />
            Settings
          </button>
        </nav>
      </aside>

      <main className="admin-content">
        <div className="content-header">
          <button className="back-btn" onClick={() => navigate('/admin/dashboard')}>
            <ArrowLeft size={20} />
            Back to Dashboard
          </button>
          <h1>Contact Messages ({contacts.length})</h1>
        </div>

        {selectedContact ? (
          <div className="contact-detail">
            <div className="detail-header">
              <button className="back-btn" onClick={() => setSelectedContact(null)}>
                <ArrowLeft size={20} />
                Back to List
              </button>
              {selectedContact.status === 'new' && (
                <button className="mark-read-btn" onClick={() => markAsRead(selectedContact.id)}>
                  <Check size={16} />
                  Mark as Read
                </button>
              )}
            </div>

            <div className="detail-card">
              <div className="detail-info">
                <div className="info-row">
                  <Mail size={18} />
                  <div>
                    <div className="info-label">Email</div>
                    <a href={`mailto:${selectedContact.email}`} className="info-value">{selectedContact.email}</a>
                  </div>
                </div>

                <div className="info-row">
                  <Calendar size={18} />
                  <div>
                    <div className="info-label">Date</div>
                    <div className="info-value">{new Date(selectedContact.created_at).toLocaleString()}</div>
                  </div>
                </div>

                <div className="info-row">
                  <Globe size={18} />
                  <div>
                    <div className="info-label">IP Address</div>
                    <div className="info-value ip-display">{selectedContact.ip_address || 'Unknown'}</div>
                  </div>
                </div>
              </div>

              <div className="message-content">
                <h3>{selectedContact.subject}</h3>
                <p>{selectedContact.message}</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="contacts-list">
            {contacts.map((contact) => (
              <div 
                key={contact.id} 
                className={`contact-card ${contact.status === 'new' ? 'unread' : ''}`}
                onClick={() => setSelectedContact(contact)}
                data-testid={`contact-card-${contact.id}`}
              >
                <div className="contact-header">
                  <div>
                    <h3>{contact.name}</h3>
                    <a href={`mailto:${contact.email}`} onClick={(e) => e.stopPropagation()}>
                      {contact.email}
                    </a>
                  </div>
                  <div className="contact-badges">
                    {contact.ip_address && (
                      <span className="ip-badge" title="Sender IP">
                        <Globe size={12} /> {contact.ip_address} {contact.country && contact.country !== 'Unknown' && `(${contact.country})`}
                      </span>
                    )}
                    {contact.status === 'new' && <span className="new-badge">New</span>}
                  </div>
                </div>
                <div className="contact-subject">{contact.subject}</div>
                <div className="contact-preview">{contact.message.substring(0, 100)}...</div>
                <div className="contact-date">
                  {new Date(contact.created_at).toLocaleDateString()}
                </div>
              </div>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminContacts;